import { USER_PREFERENCES_QUERY_KEY, useUserPreferences } from '@/hooks/use-user-preferences';
import { putUserPreferences } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
const SYNC_DEBOUNCE_MS = 1000;
/**
 * Exposes the user's report settings plus a debounced update setter. Reads
 * piggy-back on the shared useUserPreferences query so consumers (PDF preview,
 * report editor, settings UI) all share one cached fetch. PUTs only the
 * reportSettings sub-record so concurrent edits to other preference categories
 * don't get stomped, then invalidates the shared query to fan out the change.
 */
export function useReportSettings() {
    const { preferences, isLoading, isLoaded } = useUserPreferences();
    const queryClient = useQueryClient();
    const syncTimer = useRef(null);
    const settings = preferences.reportSettings;
    // Multi-key variant of `update`: one optimistic write + one debounced PUT
    // for changes that must land together (e.g. switching dictation language
    // also clears the locale, which would be invalid for the new language).
    const updatePatch = (patch) => {
        const next = { ...settings, ...patch };
        // Optimistic local update so the UI reflects the change immediately;
        // the debounced PUT + invalidate below reconciles with the server.
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, {
            ...preferences,
            reportSettings: next,
        });
        if (syncTimer.current)
            clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
            putUserPreferences({
                timezone: preferences.timezone,
                timeFormat: preferences.timeFormat,
                locale: preferences.locale,
                sidebarClinicDisplayName: preferences.sidebarClinicDisplayName,
                reportSettings: next,
            })
                .then(() => queryClient.invalidateQueries({ queryKey: USER_PREFERENCES_QUERY_KEY }))
                .catch(() => {
                /* swallow — UI keeps the optimistic state, next save retries */
            });
        }, SYNC_DEBOUNCE_MS);
    };
    const update = (key, value) => updatePatch({ [key]: value });
    // isLoaded: the stored record has been read (see useUserPreferences). A
    // writer that is not the settings screen itself checks it before update:
    // the PUT carries the whole reportSettings record.
    return { settings, update, updatePatch, isLoading, isLoaded };
}
